import { progressPct } from "@/lib/progress";
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, Network, Rows3, Minimize2, MonitorPlay, LayoutGrid, ArrowLeft, ImageDown, ChevronRight, Diamond } from "lucide-react";
import { AtRiskList } from "@/components/RoadmapView";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { toPng } from "html-to-image";

const HEALTH = { green: "#10B981", yellow: "#F59E0B", red: "#EF4444", blue: "#2563EB", gray: "#9CA3AF" };
const HEALTH_BG = { green: "#ECFDF5", yellow: "#FFFBEB", red: "#FEF2F2", blue: "#EFF6FF", gray: "#F3F4F6" };
const COL_W = 220, BOX_W = 178, BOX_H = 66, ROW_H = 96, PAD = 28, LANE_LABEL = 150;
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const fmtShort = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const fmtShortD = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

const buildTimelineModel = (tasks, phaseOrder) => {
  const dated = tasks.filter((t) => t.start_date || t.end_date);
  if (!dated.length) return null;
  const ds = dated.flatMap((t) => [t.start_date, t.end_date]).filter(Boolean).sort();
  const min = new Date(ds[0] + "T00:00:00");
  let max = new Date(ds[ds.length - 1] + "T00:00:00");
  if (max <= min) max = new Date(min.getTime() + 30 * 86400000);
  const span = max - min;
  const pct = (d) => Math.max(0, Math.min(100, ((new Date(d + "T00:00:00") - min) / span) * 100));
  const rows = phaseOrder.map((p) => {
    const ts = dated.filter((t) => t.phase === p);
    if (!ts.length) return null;
    const s = ts.map((t) => t.start_date || t.end_date).sort()[0];
    const e = ts.map((t) => t.end_date || t.start_date).sort().pop();
    const ms = ts.filter((t) => t.is_milestone).map((t) => ({ id: t.id, title: t.title, date: t.end_date || t.start_date, x: pct(t.end_date || t.start_date) }));
    return { phase: p, left: pct(s), width: Math.max(1.5, pct(e) - pct(s)), s, e, ms };
  }).filter(Boolean);
  if (!rows.length) return null;
  const months = [];
  const c = new Date(min.getFullYear(), min.getMonth(), 1);
  while (c <= max) {
    const x = ((c - min) / span) * 100;
    if (x >= 0) months.push({ x, label: c.toLocaleDateString(undefined, { month: "short", year: c.getMonth() === 0 ? "numeric" : undefined }) });
    c.setMonth(c.getMonth() + 1);
  }
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayX = today >= min && today <= max ? ((today - min) / span) * 100 : null;
  return { rows, months: months.filter((_, i) => months.length <= 16 || i % 2 === 0), todayX, min, max };
};

const TL_ROW = 26;
const timelineCanvasHeight = (model) => 44 + model.rows.length * TL_ROW + 10;

const drawTimelineOnCanvas = (ctx, model, worstByPhase, width) => {
  const pad = 24;
  const innerW = width - pad * 2;
  const px = (p) => pad + (p / 100) * innerW;
  ctx.fillStyle = "#64748b"; ctx.font = "bold 11px Arial"; ctx.textAlign = "left";
  ctx.fillText("TIMELINE", pad, 16);
  ctx.textAlign = "right";
  ctx.fillText(`${fmtShortD(model.min)} → ${fmtShortD(model.max)}`, width - pad, 16);
  ctx.font = "600 10px Arial"; ctx.fillStyle = "#94a3b8"; ctx.textAlign = "center";
  model.months.forEach((m) => ctx.fillText(m.label, px(m.x), 32));
  ctx.textAlign = "left";
  const rowsTop = 40;
  const rowsH = model.rows.length * TL_ROW;
  ctx.strokeStyle = "rgba(148,163,184,0.45)"; ctx.lineWidth = 1;
  model.months.forEach((m) => { ctx.beginPath(); ctx.moveTo(px(m.x), rowsTop); ctx.lineTo(px(m.x), rowsTop + rowsH); ctx.stroke(); });
  model.rows.forEach((r, i) => {
    const ry = rowsTop + i * TL_ROW + 3;
    const bx = px(r.left), bw = Math.max(22, (r.width / 100) * innerW);
    ctx.fillStyle = HEALTH[worstByPhase[r.phase]] || "#2563EB";
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(bx, ry, bw, TL_ROW - 6, 5); else ctx.rect(bx, ry, bw, TL_ROW - 6);
    ctx.fill();
    ctx.fillStyle = "#ffffff"; ctx.font = "bold 10px Arial";
    ctx.fillText(r.phase, bx + 6, ry + 13, Math.max(10, bw - 12));
    ctx.fillStyle = "#64748b"; ctx.font = "600 9px Arial";
    ctx.fillText(`${fmtShort(r.s)}–${fmtShort(r.e)}`, Math.min(bx + bw + 6, width - pad - 70), ry + 13);
    r.ms.forEach((m) => {
      const mx = px(m.x), my = ry + (TL_ROW - 6) / 2;
      ctx.beginPath();
      ctx.moveTo(mx, my - 5); ctx.lineTo(mx + 5, my); ctx.lineTo(mx, my + 5); ctx.lineTo(mx - 5, my); ctx.closePath();
      ctx.fillStyle = "#ffffff"; ctx.fill();
      ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 1.5; ctx.stroke();
    });
  });
  if (model.todayX != null) {
    const tx = px(model.todayX);
    ctx.strokeStyle = "#EF4444"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(tx, rowsTop - 4); ctx.lineTo(tx, rowsTop + rowsH); ctx.stroke();
    ctx.fillStyle = "#EF4444"; ctx.font = "800 8px Arial"; ctx.textAlign = "center";
    ctx.fillText("TODAY", tx, rowsTop - 7);
    ctx.textAlign = "left";
  }
};

const MapTimeline = ({ tasks, phaseOrder, worstByPhase, onPickPhase }) => {
  const model = useMemo(() => buildTimelineModel(tasks, phaseOrder), [tasks, phaseOrder]);

  if (!model) return null;
  return (
    <div className="border border-border rounded-xl bg-card px-4 pt-2 pb-3" data-testid="map-timeline">
      <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-1">
        <span>Timeline</span>
        <span className="tabular-nums normal-case tracking-normal font-semibold">{fmtShort(model.min.toISOString().slice(0, 10))} → {fmtShort(model.max.toISOString().slice(0, 10))}</span>
      </div>
      <div className="relative" style={{ height: 18 }}>
        {model.months.map((m, i) => (
          <span key={`${m.label}-${m.x}`} className="absolute text-[10px] font-semibold text-muted-foreground -translate-x-1/2 whitespace-nowrap" style={{ left: `${m.x}%`, top: 0 }}>{m.label}</span>
        ))}
      </div>
      <div className="relative">
        {model.months.map((m, i) => (
          <span key={`grid-${m.x}`} className="absolute top-0 bottom-0 border-l border-border/60" style={{ left: `${m.x}%` }} aria-hidden />
        ))}
        {model.todayX != null && (
          <span className="absolute top-0 bottom-0 border-l-2 border-red-500 z-10" style={{ left: `${model.todayX}%` }} title="Today" data-testid="map-timeline-today">
            <span className="absolute -top-0.5 -translate-x-1/2 text-[8px] font-extrabold text-red-500 bg-card px-0.5 rounded">TODAY</span>
          </span>
        )}
        <div className="relative space-y-1 py-1">
          {model.rows.map((r) => {
            const color = HEALTH[worstByPhase[r.phase]] || "#2563EB";
            return (
              <div key={r.phase} className="relative h-6" data-testid={`map-timeline-row-${slug(r.phase)}`}>
                <button type="button" onClick={() => onPickPhase?.(r.phase)}
                  className="absolute h-full rounded-md flex items-center px-2 overflow-hidden hover:brightness-110 transition-[filter]"
                  style={{ left: `${r.left}%`, width: `${r.width}%`, background: color, minWidth: 24 }}
                  title={`${r.phase}: ${fmtShort(r.s)} → ${fmtShort(r.e)} — click to open this phase`}
                  data-testid={`map-timeline-phase-${slug(r.phase)}`}>
                  <span className="text-[10px] font-bold text-white truncate">{r.phase}</span>
                </button>
                {r.ms.map((m) => (
                  <span key={m.id} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-2.5 w-2.5 rotate-45 bg-white border-2 z-[5] pointer-events-auto"
                    style={{ left: `${m.x}%`, borderColor: "#0f172a" }}
                    title={`◆ ${m.title} — ${fmtShort(m.date)}`} data-testid={`map-timeline-ms-${m.id}`} />
                ))}
                <span className="absolute top-1/2 -translate-y-1/2 text-[9px] font-semibold text-muted-foreground whitespace-nowrap" style={{ left: `calc(${Math.min(r.left + r.width, 100)}% + 6px)` }}>
                  {fmtShort(r.s)}–{fmtShort(r.e)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function ProjectMap({ projectId, phases = [], onOpenTask, progressStyle }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("phases");
  const [selPhase, setSelPhase] = useState(null);
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [singlePage, setSinglePage] = useState(false);
  const [pageZoom, setPageZoom] = useState(1);
  const [detailZoom, setDetailZoom] = useState(0);   // 0 = auto-fit
  const [pptBusy, setPptBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${projectId}/critical-path`).then(({ data }) => setData(data)).catch(() => setData({ tasks: [], edges: [] }));
  }, [projectId]);

  const phaseOrder = useMemo(() => {
    const all = data?.tasks || [];
    return (phases.length ? phases : [...new Set(all.map((n) => n.phase))]).filter((p) => all.some((t) => t.phase === p));
  }, [data, phases]);

  const phaseSummary = useMemo(() => {
    if (!data) return [];
    return phaseOrder.map((p) => {
      const ts = data.tasks.filter((t) => t.phase === p);
      const avg = progressPct(ts, progressStyle);
      const mix = { red: 0, yellow: 0, green: 0, blue: 0, gray: 0 };
      ts.forEach((t) => { mix[t.health] = (mix[t.health] || 0) + 1; });
      const worst = mix.red ? "red" : mix.yellow ? "yellow" : avg >= 100 ? "blue" : mix.green ? "green" : "gray";
      return { phase: p, n: ts.length, avg, mix, worst, ms: ts.filter((t) => t.is_milestone).length, crit: ts.filter((t) => t.is_critical).length };
    });
  }, [data, phaseOrder, progressStyle]);

  const graph = useMemo(() => {
    if (!data) return null;
    const nodes = selPhase ? (data.tasks || []).filter((t) => t.phase === selPhase) : (data.tasks || []);
    const ids = new Set(nodes.map((n) => n.id));
    const edges = (data.edges || []).filter((e) => ids.has(e.from) && ids.has(e.to));
    // longest-path layering
    const layer = {}; nodes.forEach((n) => (layer[n.id] = 0));
    for (let i = 0; i < nodes.length + 1; i++) {
      let ch = false;
      edges.forEach((e) => { if (layer[e.to] < layer[e.from] + 1) { layer[e.to] = layer[e.from] + 1; ch = true; } });
      if (!ch) break;
    }
    const pos = {};
    if (selPhase || mode !== "swimlane") {
      const perLayer = {};
      nodes.slice().sort((a, b) => (phaseOrder.indexOf(a.phase) - phaseOrder.indexOf(b.phase)) || a.title.localeCompare(b.title))
        .forEach((n) => {
          const l = layer[n.id];
          perLayer[l] = (perLayer[l] || 0);
          pos[n.id] = { x: l * COL_W + PAD, y: perLayer[l] * ROW_H + PAD };
          perLayer[l] += 1;
        });
      const w = Math.max(...Object.values(layer), 0) * COL_W + BOX_W + PAD * 2;
      const h = Math.max(1, ...Object.values(perLayer)) * ROW_H + PAD * 2;
      return { nodes, edges, pos, w, h, lanes: null };
    }
    // swimlane by phase
    const slotKey = {}; const laneStack = {};
    phaseOrder.forEach((p) => (laneStack[p] = 0));
    nodes.forEach((n) => {
      const k = `${n.phase}:${layer[n.id]}`;
      slotKey[k] = (slotKey[k] || 0) + 1;
      laneStack[n.phase] = Math.max(laneStack[n.phase] || 0, slotKey[k]);
    });
    const laneTop = {}; let cy = PAD;
    phaseOrder.forEach((p) => { laneTop[p] = cy; cy += Math.max(1, laneStack[p] || 1) * (BOX_H + 14) + 30; });
    const slotCount = {};
    nodes.forEach((n) => {
      const k = `${n.phase}:${layer[n.id]}`;
      const slot = (slotCount[k] = (slotCount[k] || 0)); slotCount[k] += 1;
      pos[n.id] = { x: LANE_LABEL + layer[n.id] * COL_W + PAD, y: laneTop[n.phase] + 24 + slot * (BOX_H + 14) };
    });
    const w = LANE_LABEL + Math.max(...Object.values(layer), 0) * COL_W + BOX_W + PAD * 2;
    return { nodes, edges, pos, w, h: cy, lanes: phaseOrder.map((p) => ({ phase: p, top: laneTop[p], height: Math.max(1, laneStack[p] || 1) * (BOX_H + 14) + 30 })) };
  }, [data, mode, phaseOrder, selPhase]);

  useEffect(() => {
    if (!singlePage || !graph || selPhase) { setPageZoom(1); return; }
    const t = setTimeout(() => {
      const w = wrapRef.current;
      if (!w) return;
      const availW = Math.max(300, w.clientWidth - 8);
      const availH = Math.max(240, window.innerHeight - w.getBoundingClientRect().top - 40);
      const gw = Math.max(graph.w, 600), gh = Math.max(graph.h, 300);
      setPageZoom(Math.min(1, Math.round(Math.min(availW / gw, availH / gh) * 100) / 100));
    }, 100);
    return () => clearTimeout(t);
  }, [singlePage, graph, selPhase]);

  // auto-fit zoom (upscaled for readability) in phase detail
  const autoDetailZoom = useMemo(() => {
    if (!selPhase || !graph) return 1;
    const availW = (wrapRef.current?.clientWidth || 1100) - 8;
    return Math.min(1.8, Math.max(0.5, Math.round((availW / Math.max(graph.w, 400)) * 100) / 100));
  }, [selPhase, graph]);
  const effDetailZoom = detailZoom || autoDetailZoom;

  const worstByPhase = useMemo(() => Object.fromEntries(phaseSummary.map((p) => [p.phase, p.worst])), [phaseSummary]);
  const tlModel = useMemo(() => (data ? buildTimelineModel(data.tasks || [], phaseOrder) : null), [data, phaseOrder]);

  const svgToPng = async () => {
    const svg = innerRef.current?.querySelector("svg");
    if (!svg) throw new Error("Map not rendered yet");
    const w = svg.width.baseVal.value, h = svg.height.baseVal.value;
    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("style", "font-family: Arial, Helvetica, sans-serif; background: #ffffff");
    const xml = new XMLSerializer().serializeToString(clone);
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error("SVG rasterize failed")); img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml); });
    const tlH = tlModel ? timelineCanvasHeight(tlModel) : 0;
    const totalH = h + tlH;
    const ratio = Math.max(0.5, Math.min(2, 8000 / w, 8000 / totalH));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * ratio);
    canvas.height = Math.round(totalH * ratio);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(ratio, ratio);
    if (tlModel) {
      drawTimelineOnCanvas(ctx, tlModel, worstByPhase, w);
      ctx.strokeStyle = "rgba(148,163,184,0.5)"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, tlH - 4); ctx.lineTo(w, tlH - 4); ctx.stroke();
    }
    ctx.drawImage(img, 0, tlH, w, h);
    ctx.restore();
    return canvas.toDataURL("image/png");
  };

  const downloadPng = async () => {
    try {
      const url = await svgToPng();
      const a = document.createElement("a");
      a.href = url;
      a.download = selPhase ? `visual-map-${slug(selPhase)}.png` : "visual-map.png";
      a.click();
      toast.success("PNG downloaded — drop it into any tool");
    } catch (e) {
      toast.error(`PNG export failed${e?.message ? `: ${e.message}` : ""}`);
    }
  };

  const exportPpt = async () => {
    setPptBusy(true);
    try {
      const PptxGenJS = (await import("pptxgenjs")).default;
      let dataUrl;
      try {
        dataUrl = await svgToPng();
      } catch {
        const node = innerRef.current;
        dataUrl = await toPng(node, { backgroundColor: "#ffffff", pixelRatio: 2, width: node.scrollWidth, height: node.scrollHeight, style: { transform: "none", overflow: "visible" }, skipFonts: true });
      }
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataUrl; });
      const pptx = new PptxGenJS();
      pptx.layout = "LAYOUT_WIDE"; // 13.33 x 7.5 in
      const slide = pptx.addSlide();
      slide.addText(selPhase ? `Visual Map — ${selPhase}` : "Visual Map", { x: 0.4, y: 0.15, w: 12.5, h: 0.5, fontSize: 20, bold: true, color: "132A5E" });
      const maxW = 12.6, maxH = 6.4;
      const ratio = img.width / img.height;
      let w = maxW, h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      slide.addImage({ data: dataUrl, x: (13.33 - w) / 2, y: 0.75 + (maxH - h) / 2, w, h });
      await pptx.writeFile({ fileName: selPhase ? `visual-map-${slug(selPhase)}.pptx` : "visual-map.pptx" });
      toast.success("Downloaded as PowerPoint slide");
    } catch (e) {
      console.error("Visual map PPT export failed:", e);
      toast.error(`PPT export failed${e?.message ? `: ${e.message}` : ""} — try refreshing the page`);
    } finally {
      setPptBusy(false);
    }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!graph || (data.tasks || []).length === 0) {
    return <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="map-empty"><Network className="h-8 w-8 text-primary mx-auto mb-3" /><p className="text-muted-foreground">Add tasks and dependencies to see the project map.</p></div>;
  }

  const anchor = (id, side) => {
    const p = graph.pos[id];
    return { x: p.x + (side === "out" ? BOX_W : 0), y: p.y + BOX_H / 2 };
  };

  const showGraph = selPhase || mode !== "phases";
  const scale = selPhase ? effDetailZoom : (singlePage ? pageZoom : 1);
  const scaled = scale !== 1;

  return (
    <div className="space-y-4" data-testid="project-map">
      <MapTimeline tasks={data.tasks || []} phaseOrder={phaseOrder}
        worstByPhase={worstByPhase}
        onPickPhase={(p) => { setSelPhase(p); }} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-4 text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-6 rounded" style={{ background: "#EF4444" }} /> Critical path</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#2563EB" }} /> Complete</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#10B981" }} /> On track</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#F59E0B" }} /> At risk</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#EF4444" }} /> Off track</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#9CA3AF" }} /> No committed date</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showGraph && (
            <>
              <Button variant="outline" size="sm" className="gap-2" onClick={downloadPng} title="Download this view as a PNG image" data-testid="map-export-png">
                <ImageDown className="h-4 w-4" /> PNG
              </Button>
              <Button variant="outline" size="sm" className="gap-2" onClick={exportPpt} disabled={pptBusy} title="Download as a PowerPoint slide (.pptx)" data-testid="map-export-ppt">
                {pptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MonitorPlay className="h-4 w-4" />} PPT
              </Button>
            </>
          )}
          {showGraph && !selPhase && (
            <Button variant="outline" size="sm" className={`gap-2 ${singlePage ? "border-primary text-primary bg-primary/5" : ""}`} onClick={() => setSinglePage((v) => !v)} title="Scale the whole map to fit your screen in a single page view" data-testid="map-single-page-toggle">
              <Minimize2 className="h-4 w-4" /> {singlePage ? "Single page ✓" : "Single page"}
            </Button>
          )}
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            <button onClick={() => { setMode("phases"); setSelPhase(null); }} className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${mode === "phases" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid="map-mode-phases"><LayoutGrid className="h-4 w-4" /> Phases</button>
            <button onClick={() => { setMode("network"); setSelPhase(null); }} className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${mode === "network" && !selPhase ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid="map-mode-network"><Network className="h-4 w-4" /> Network</button>
            <button onClick={() => { setMode("swimlane"); setSelPhase(null); }} className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${mode === "swimlane" && !selPhase ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} data-testid="map-mode-swimlane"><Rows3 className="h-4 w-4" /> Swimlanes</button>
          </div>
        </div>
      </div>

      {selPhase && (
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="map-phase-detail-header">
          <Button variant="ghost" size="sm" className="gap-2" onClick={() => setSelPhase(null)} data-testid="map-back-to-phases"><ArrowLeft className="h-4 w-4" /> All phases</Button>
          <h3 className="font-display text-lg font-bold tracking-tight">{selPhase}</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Zoom</span>
            <input type="range" min="50" max="200" step="10" value={Math.round(effDetailZoom * 100)} onChange={(e) => setDetailZoom(Number(e.target.value) / 100)} className="w-28 accent-primary" data-testid="map-detail-zoom" />
            <span className="text-xs tabular-nums text-muted-foreground w-10">{Math.round(effDetailZoom * 100)}%</span>
          </div>
        </div>
      )}

      {!showGraph ? (
        <div className="flex flex-wrap items-stretch gap-y-5" data-testid="map-phase-blocks">
          {phaseSummary.map((p, i) => (
            <div key={p.phase} className="flex items-center">
              <button onClick={() => setSelPhase(p.phase)} className="text-left w-64 border border-border rounded-xl p-5 bg-card hover:shadow-md hover:border-foreground/30 transition-all group" style={{ borderTop: `5px solid ${HEALTH[p.worst]}` }} data-testid={`map-phase-block-${slug(p.phase)}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Phase {i + 1}</p>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="font-display font-bold text-base leading-tight mt-1 min-h-[2.4em]">{p.phase}</p>
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.avg}%`, background: p.avg >= 100 ? "#2563EB" : HEALTH[p.worst] }} /></div>
                  <span className="text-sm font-extrabold tabular-nums" style={{ color: p.avg >= 100 ? "#2563EB" : undefined }}>{p.avg}%</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[11px]">
                  {["blue", "green", "yellow", "red", "gray"].filter((k) => p.mix[k]).map((k) => (
                    <span key={k} className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: HEALTH[k] }} /> {p.mix[k]}</span>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
                  {p.n} task{p.n === 1 ? "" : "s"}{p.ms ? <> · <Diamond className="h-2.5 w-2.5 inline" /> {p.ms} milestone{p.ms === 1 ? "" : "s"}</> : null}{p.crit ? ` · ★ ${p.crit} critical` : ""}
                </p>
              </button>
              {i < phaseSummary.length - 1 && (
                <svg width="34" height="16" className="shrink-0 text-muted-foreground/50 mx-0.5"><path d="M0 8 H26 M20 2 L28 8 L20 14" stroke="currentColor" strokeWidth="2" fill="none" /></svg>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div ref={wrapRef} className={`border border-border rounded-lg bg-secondary/20 ${scaled && scale < 1 ? "overflow-hidden" : "overflow-auto"}`} style={scaled ? { height: Math.max(graph.h, 300) * scale + 6, maxHeight: selPhase ? undefined : undefined } : { maxHeight: "72vh" }}>
          <div ref={innerRef} style={scaled ? { transform: `scale(${scale})`, transformOrigin: "top left", width: Math.max(graph.w, 600) } : { width: "max-content" }}>
          <svg width={Math.max(graph.w, 600)} height={Math.max(graph.h, 300)} style={{ display: "block" }}>
            <defs>
              <marker id="map-arrow" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3 L0,6 Z" fill="#94a3b8" /></marker>
              <marker id="map-arrow-crit" markerWidth="10" markerHeight="10" refX="7" refY="3" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3 L0,6 Z" fill="#EF4444" /></marker>
              <filter id="map-shadow" x="-20%" y="-20%" width="140%" height="160%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#0f172a" floodOpacity="0.14" />
              </filter>
            </defs>

            {graph.lanes && graph.lanes.map((ln, i) => (
              <g key={ln.phase}>
                <rect x={0} y={ln.top} width={graph.w} height={ln.height} fill={i % 2 ? "rgba(148,163,184,0.06)" : "transparent"} />
                <text x={10} y={ln.top + 18} fontSize="11" fontWeight="700" fill="#475569" style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>{ln.phase}</text>
              </g>
            ))}

            {graph.edges.map((e, i) => {
              const a = anchor(e.from, "out"), b = anchor(e.to, "in");
              const midx = a.x + Math.max(20, (b.x - a.x) / 2);
              const d = `M ${a.x} ${a.y} H ${midx} V ${b.y} H ${b.x - 8}`;
              return <path key={`${e.from}-${e.to}`} d={d} fill="none" stroke={e.critical ? "#EF4444" : "#94a3b8"} strokeWidth={e.critical ? 3 : 1.4} strokeDasharray={e.critical ? "0" : "4 3"} markerEnd={`url(#${e.critical ? "map-arrow-crit" : "map-arrow"})`} opacity={e.critical ? 1 : 0.55} />;
            })}

            {graph.nodes.map((n) => {
              const p = graph.pos[n.id];
              const hc = HEALTH[n.health] || "#64748b";
              const bg = HEALTH_BG[n.health] || "#fff";
              const title = n.title.length > 22 ? n.title.slice(0, 21) + "…" : n.title;
              const pct = n.percent_complete != null ? Math.max(0, Math.min(100, n.percent_complete)) : (n.health === "blue" || n.status === "done" ? 100 : 0);
              // Milestones render as a sharp diamond
              if (n.is_milestone) {
                const s = 44, cx = BOX_W / 2, cy = BOX_H / 2;
                return (
                  <g key={n.id} transform={`translate(${p.x},${p.y})`} style={{ cursor: "pointer" }} onClick={() => onOpenTask?.(n)} data-testid={`map-node-${n.id}`}>
                    <path d={`M ${cx} ${cy - s} L ${cx + s} ${cy} L ${cx} ${cy + s} L ${cx - s} ${cy} Z`} fill={bg} stroke={n.is_critical ? "#DC2626" : hc} strokeWidth={n.is_critical ? 3 : 2} filter="url(#map-shadow)" />
                    <path d={`M ${cx} ${cy - 12} l 5 8 h -10 Z`} fill={hc} />
                    <text x={cx} y={cy + 6} fontSize="9" fontWeight="800" fill="#0f172a" textAnchor="middle">{title}</text>
                    <text x={cx} y={cy + 19} fontSize="10" fill={hc} fontWeight="800" textAnchor="middle">{`${pct}%`}</text>
                    {["at_risk", "jeopardy"].includes(n.track_status) && (
                      <text x={cx} y={cy + 31} fontSize="7.5" fontWeight="800" textAnchor="middle" fill={n.track_status === "jeopardy" ? "#EF4444" : "#D97706"} data-testid={`map-track-${n.id}`}>
                        {n.track_status === "jeopardy" ? "JEOPARDY" : "AT RISK"}
                      </text>
                    )}
                  </g>
                );
              }
              return (
                <g key={n.id} transform={`translate(${p.x},${p.y})`} style={{ cursor: "pointer" }} onClick={() => onOpenTask?.(n)} data-testid={`map-node-${n.id}`}>
                  <rect width={BOX_W} height={BOX_H} rx="10" fill={hc} stroke={n.is_critical ? "#DC2626" : hc} strokeWidth={n.is_critical ? 3 : 1} filter="url(#map-shadow)" />
                  <text x="12" y="17" fontSize="11" fontWeight="800" fill="#ffffff">{title}</text>
                  {n.is_critical && <text x={BOX_W - 8} y="16" fontSize="11" fill="#ffffff" fontWeight="900" textAnchor="end">★</text>}
                  {["at_risk", "jeopardy"].includes(n.track_status) && (
                    <g data-testid={`map-track-${n.id}`}>
                      <rect x={BOX_W - (n.is_critical ? 78 : 62)} y="24" width="52" height="13" rx="6.5" fill="#ffffff" opacity="0.94" />
                      <text x={BOX_W - (n.is_critical ? 52 : 36)} y="33.5" fontSize="7.5" fontWeight="800" textAnchor="middle" fill={n.track_status === "jeopardy" ? "#EF4444" : "#D97706"}>
                        {n.track_status === "jeopardy" ? "JEOPARDY" : "AT RISK"}
                      </text>
                    </g>
                  )}
                  <text x="12" y="34" fontSize="9.5" fill="rgba(255,255,255,0.85)">{n.phase?.length > 24 ? n.phase.slice(0, 23) + "…" : n.phase}</text>
                  <text x="12" y="49" fontSize="11.5" fill="#ffffff" fontWeight="800">{`${pct}%${n.expected_percent != null ? ` / exp ${n.expected_percent}%` : ""}`}</text>
                  {/* progress bar */}
                  <rect x="12" y="55" width={BOX_W - 24} height="5" rx="2.5" fill="rgba(255,255,255,0.35)" />
                  <rect x="12" y="55" width={(BOX_W - 24) * pct / 100} height="5" rx="2.5" fill="#ffffff" />
                </g>
              );
            })}
          </svg>
          </div>
        </div>
      )}
      <AtRiskList tasks={selPhase ? (data.tasks || []).filter((t) => t.phase === selPhase) : data.tasks} />
    </div>
  );
}
